import { useMemo } from 'react';
import { graphql } from 'react-relay';
import type { GraphQLTaggedNode } from 'react-relay';

import { useEditorContentFragment } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';
import type { EditorContentFragmentResult } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';

import type { useShareAndRestrictButtonDataQuery$data } from './__generated__/useShareAndRestrictButtonDataQuery.graphql.ts';
import type { useShareAndRestrictButtonFragment_content$key } from './__generated__/useShareAndRestrictButtonFragment_content.graphql.ts';
import type { useShareAndRestrictButtonFragment_query$key } from './__generated__/useShareAndRestrictButtonFragment_query.graphql.ts';

// useShareAndRestrictButtonDataQuery's singleContent selection for EditorContentUnifiedQuery.
const contentFragmentNode: GraphQLTaggedNode = graphql`
	fragment useShareAndRestrictButtonFragment_content on Content @catch {
		id
		hasInheritedRestrictions
		hasRestrictions
		hasViewRestrictions
		title
	}
`;

// Its publicLinkPage selection, on Query.
const publicLinkFragmentNode: GraphQLTaggedNode = graphql`
	fragment useShareAndRestrictButtonFragment_query on Query
	@argumentDefinitions(contentId: { type: "ID!" })
	@catch {
		publicLinkPage(pageId: $contentId) {
			pageId
			pageStatus
			accessType
		}
	}
`;

/**
 * The share and restrict button data from EditorContentUnifiedQuery in the shape of
 * useShareAndRestrictButtonDataQuery data. When it is provided, its reader skips
 * useShareAndRestrictButtonDataQuery.
 */
export function useShareAndRestrictButtonFragment({
	contentId,
	isContentTypePublicLinkEligible,
}: {
	contentId: string | undefined;
	isContentTypePublicLinkEligible: boolean;
}): EditorContentFragmentResult<useShareAndRestrictButtonDataQuery$data> {
	const content = useEditorContentFragment<useShareAndRestrictButtonFragment_content$key>(
		'shareAndRestrictButtonFragment',
		contentFragmentNode,
		{ contentId },
	);
	// The legacy query selects publicLinkPage only for content types with public links.
	const publicLink = useEditorContentFragment<useShareAndRestrictButtonFragment_query$key>(
		'shareAndRestrictButtonPublicLinkFragment',
		publicLinkFragmentNode,
		{ contentId, skip: !isContentTypePublicLinkEligible },
	);

	return useMemo(
		() => ({
			isProvided: content.isProvided,
			loading: content.loading,
			data: content.data && {
				singleContent: content.data,
				...(isContentTypePublicLinkEligible
					? { publicLinkPage: publicLink.data?.publicLinkPage }
					: {}),
			},
			error: content.error ?? publicLink.error,
		}),
		[content, publicLink, isContentTypePublicLinkEligible],
	);
}
